import type {
  DynatraceTelemetryIntegrationOptions,
  TelemetryIntegrationWarning,
} from "./types";
import type {
  TelemetryIntegrationAuditPayload,
  TelemetryIntegrationDispatchContext,
  TelemetryIntegrationProviderAdapter,
} from "./telemetryIntegration";

const DEFAULT_GLOBAL_KEY = "dtrum";
const DEFAULT_ACTION_NAME = "sofya transcription audit";
const MAX_SHORT_STRING_LENGTH = 100;

interface DtrumLike {
  enterAction?: (actionName: string) => number;
  leaveAction?: (actionId: number) => void;
  sendSessionProperties?: (
    javaLong?: Record<string, number>,
    date?: Record<string, Date>,
    shortString?: Record<string, string>,
    javaDouble?: Record<string, number>
  ) => unknown;
  addActionProperties?: (
    parentActionId: number,
    javaLong?: Record<string, number>,
    date?: Record<string, Date>,
    shortString?: Record<string, string>,
    javaDouble?: Record<string, number>
  ) => unknown;
  reportError?: (error: string | Error, parentActionId?: number) => void;
  reportCustomError?: (
    key: string,
    value: string,
    hint?: string,
    parentingInfo?: number | boolean
  ) => void;
}

type WarningCallback = (warning: TelemetryIntegrationWarning) => void;

const normalizeKey = (key: string): string =>
  key
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "_")
    .replace(/^_+|_+$/g, "");

const truncateShortString = (value: string): string =>
  value.length > MAX_SHORT_STRING_LENGTH
    ? value.slice(0, MAX_SHORT_STRING_LENGTH)
    : value;

const putShortString = (
  target: Record<string, string>,
  key: string,
  value: string | null | undefined
) => {
  if (typeof value !== "string" || value.length === 0) {
    return;
  }

  target[normalizeKey(key)] = truncateShortString(value);
};

const putJavaLong = (
  target: Record<string, number>,
  key: string,
  value: number | null | undefined
) => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return;
  }

  target[normalizeKey(key)] = Math.trunc(value);
};

const putJavaDouble = (
  target: Record<string, number>,
  key: string,
  value: number | null | undefined
) => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return;
  }

  target[normalizeKey(key)] = value;
};

const toPropertyMaps = (
  payload: TelemetryIntegrationAuditPayload,
  context: TelemetryIntegrationDispatchContext
) => {
  const javaLong: Record<string, number> = {};
  const javaDouble: Record<string, number> = {};
  const shortString: Record<string, string> = {};

  putShortString(shortString, "sofya.session_id", payload.sessionId);
  putShortString(shortString, "sofya.label", payload.label);
  putShortString(shortString, "sofya.requested_provider", payload.requestedProvider);
  putShortString(shortString, "sofya.resolved_provider", payload.resolvedProvider ?? "unknown");
  putShortString(shortString, "sofya.outcome", payload.report.summary.outcome);
  putShortString(shortString, "sofya.dispatch_reason", context.reason);

  putJavaLong(javaLong, "sofya.session_duration_ms", payload.sessionDurationMs);
  putJavaLong(
    javaLong,
    "sofya.final_transcript_count",
    payload.report.summary.finalTranscriptCount
  );
  putJavaLong(javaLong, "sofya.partial_transcript_count", payload.report.summary.partialTranscriptCount);
  putJavaLong(javaLong, "sofya.findings_count", payload.report.findings.length);
  putJavaLong(javaLong, "sofya.timeline_phase_count", payload.report.timeline.phases.length);
  putJavaLong(javaLong, "sofya.timeline_incident_count", payload.report.timeline.incidents.length);
  putJavaLong(
    javaLong,
    "sofya.reconnect_count",
    payload.report.summary.reconnectCount
  );
  putJavaLong(
    javaLong,
    "sofya.terminal_disconnect_count",
    payload.report.summary.terminalDisconnectCount
  );

  putJavaDouble(
    javaDouble,
    "sofya.avg_final_latency_ms",
    payload.report.summary.avgFinalLatencyMs
  );
  putJavaDouble(
    javaDouble,
    "sofya.avg_recovery_duration_ms",
    payload.report.summary.avgRecoveryDurationMs
  );
  putJavaDouble(
    javaDouble,
    "sofya.offline_duration_ms",
    payload.report.summary.offlineDurationMs
  );

  return {
    javaLong,
    javaDouble,
    shortString,
  };
};

class DynatraceTelemetryIntegrationProvider implements TelemetryIntegrationProviderAdapter {
  public readonly provider = "dynatrace" as const;
  private readonly globalKey: string;
  private readonly actionName: string;
  private readonly onWarning: WarningCallback;

  constructor(
    options: DynatraceTelemetryIntegrationOptions | undefined,
    onWarning: WarningCallback
  ) {
    this.globalKey = options?.globalKey?.trim() || DEFAULT_GLOBAL_KEY;
    this.actionName = options?.actionName?.trim() || DEFAULT_ACTION_NAME;
    this.onWarning = onWarning;
  }

  public dispatchAudit(
    payload: TelemetryIntegrationAuditPayload,
    context: TelemetryIntegrationDispatchContext
  ): void {
    const runtimeTarget = globalThis as Record<string, unknown>;
    const dtrum = runtimeTarget[this.globalKey] as DtrumLike | undefined;

    if (!dtrum) {
      this.onWarning({
        provider: "dynatrace",
        code: "runtime_missing",
        message:
          `Dynatrace telemetry integration is enabled but '${this.globalKey}' was not found on global scope.`,
        timestamp: Date.now(),
        detail: {
          globalKey: this.globalKey,
        },
      });
      return;
    }

    const properties = toPropertyMaps(payload, context);
    let actionId: number | null = null;

    try {
      if (typeof dtrum.enterAction === "function") {
        const enteredActionId = dtrum.enterAction(this.actionName);
        if (typeof enteredActionId === "number" && enteredActionId > 0) {
          actionId = enteredActionId;
        }
      }

      if (typeof dtrum.sendSessionProperties === "function") {
        dtrum.sendSessionProperties(
          properties.javaLong,
          undefined,
          properties.shortString,
          properties.javaDouble
        );
      }

      if (
        actionId !== null &&
        typeof dtrum.addActionProperties === "function"
      ) {
        dtrum.addActionProperties(
          actionId,
          properties.javaLong,
          undefined,
          properties.shortString,
          properties.javaDouble
        );
      }

      const attachToCurrent = actionId ?? true;

      if (
        (payload.report.summary.outcome === "failed" ||
          payload.report.summary.outcome === "degraded_unrecovered") &&
        typeof dtrum.reportError === "function"
      ) {
        dtrum.reportError(
          `Sofya audit outcome: ${payload.report.summary.outcome}`,
          actionId ?? undefined
        );
      }

      const reportCustomError = dtrum.reportCustomError;
      if (typeof reportCustomError === "function") {
        payload.report.findings.forEach((finding) => {
          reportCustomError(
            "sofya_audit_finding",
            `${finding.code}:${finding.severity}`,
            finding.message,
            attachToCurrent
          );
        });
      }
    } catch (error) {
      this.onWarning({
        provider: "dynatrace",
        code: "dispatch_failed",
        message: "Dynatrace telemetry dispatch failed.",
        timestamp: Date.now(),
        detail: {
          errorMessage: error instanceof Error ? error.message : String(error),
        },
      });
    } finally {
      if (actionId !== null && typeof dtrum.leaveAction === "function") {
        try {
          dtrum.leaveAction(actionId);
        } catch (error) {
          this.onWarning({
            provider: "dynatrace",
            code: "dispatch_failed",
            message: "Dynatrace action finalization failed.",
            timestamp: Date.now(),
            detail: {
              errorMessage: error instanceof Error ? error.message : String(error),
            },
          });
        }
      }
    }
  }
}

export const createDynatraceTelemetryIntegrationProvider = (
  options: DynatraceTelemetryIntegrationOptions | undefined,
  onWarning: WarningCallback
): TelemetryIntegrationProviderAdapter =>
  new DynatraceTelemetryIntegrationProvider(options, onWarning);
